import React, {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useState,
} from "react";
import { useSelector } from "react-redux";
import Message from "../Message";
import MessageLoader from "../MessageLoader";
import HarrisChainOfThought from "../HarrisChainOfThought";
import {
    selectMessages,
    selectIsAssistantResponding,
    selectIsTyping,
    selectErrorMessage,
    selectTaskStatusMessage
} from "../../Selectors/AssistantSelector";
import AssistantThinkingLoader from "../AssistantThinkingLoader";
import NewsArticlesAccordian from "../NewsArticlesAccordian";
import SourcesPill from "../SourcesPill";
import "./MessageContent.scss"
import Welcome from "../Welcome";
import ErrorBanner from '../ErrorBanner'

const MessageContent = forwardRef((props, ref) => {
    const messages = useSelector(selectMessages);
    const isUserTyping = useSelector(selectIsTyping);
    const isAssistantResponding = useSelector(selectIsAssistantResponding);
    const errorMessage = useSelector(selectErrorMessage);
    const taskStatusMessage = useSelector(selectTaskStatusMessage);
    const [statusTrail, setStatusTrail] = useState([]);
    const [isTaskStatusClosed, setIsTaskStatusClosed] = useState(false);

    const messageEndRef = useRef(null);
    const previousTypes = [];

    const getPreviousMsgType = (msgType, index) => {
        previousTypes.push(msgType);
        return index === 0 ? "" : previousTypes[index - 1];
    };

    const scrollToBottom = () => {
        const container = messageEndRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    };

    useImperativeHandle(ref, () => ({
        scrollToBottom,
    }));

    useEffect(() => {
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);
        return () => clearTimeout(timer);
    }, [messages, isAssistantResponding, errorMessage]);

    useEffect(() => {
        if (!statusTrail.length) return;
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);
        return () => clearTimeout(timer);
    }, [statusTrail]);

    useEffect(() => {
        if (!taskStatusMessage) return;
        if (taskStatusMessage?.type === "task_status_end") {
            setStatusTrail([]);
            setIsTaskStatusClosed(true);
            return;
        }
        setIsTaskStatusClosed(false);
        setStatusTrail((prev) => {
            const next = [...prev, taskStatusMessage];
            const MAX_STATUSES = 6;
            return next.slice(-MAX_STATUSES);
        });
    }, [taskStatusMessage]);

    useEffect(() => {
        if (isAssistantResponding) {
            setIsTaskStatusClosed(false);
            return;
        }
        setStatusTrail([]);
        setIsTaskStatusClosed(false);
    }, [isAssistantResponding]);

    useEffect(() => {
        if (messages.length !== 0) return;
        setStatusTrail([]);
        setIsTaskStatusClosed(true);
    }, [messages.length]);

    const statusSteps = statusTrail.length
        ? statusTrail.map((status) => ({
            title: status?.taskTitle || status?.stage || "Working on it...",
            items: status?.taskMessage ? [status.taskMessage] : [],
        }))
        : ["Working on it..."];
    const showChainOfThought =
        !isTaskStatusClosed && (isAssistantResponding || statusTrail.length > 0);

    return (
        <div className="card-content">
            {messages.length === 0 ? (
                <Welcome />
            ) : (
                <ul className="msg-container" ref={messageEndRef}>
                    {messages.map((msg, index) => (
                        <Message
                            key={index}
                            msg={msg}
                            previousType={getPreviousMsgType(msg.type, index)}
                            scrollToBottom={scrollToBottom}
                        />

                    ))}
                    {isUserTyping && <MessageLoader type="You" />}
                    {errorMessage && <ErrorBanner message={errorMessage} />}
                </ul>
            )}
            {showChainOfThought && (
                <div className="cot-wrapper">
                    <HarrisChainOfThought
                        steps={statusSteps}
                    />
                </div>
            )}
        </div>
    );

});

export default MessageContent;
